import { CoreMenuItem, CoreRadioGroup, CoreTypography } from '@youscience/core';
import {
  StyledApplication,
  StyledApplicationButtonGroup,
  StyledApplicationFormControl,
  StyledApplicationScore,
  StyledApplicationScoreNumber,
  StyledApplicationScoreRadio,
  StyledApplicationSection,
  StyledApplicationSelect,
  StyledCardDivider,
  sxStyles,
} from './EditSchoolCardApplication.styles';

import { FormState } from '@interfaces/applications';
import { InputLabel, SelectChangeEvent } from '@mui/material';
import { AdmissionStatus, ApplicationStatus, ApplicationType, DeadlineType, Decision } from '@common-types';
import { SCHOOL_RANKING_DATA } from '@constants/applyingSchoolsPlanner';
import ActionButton from '@components/ActionButton';
import { StyledCardInfoLine } from '../../PlannerSchoolCard.styles';

interface EditSchoolCardApplicationProps {
  formState: FormState;
  handleOnSave: () => void;
  handleOnCancel: () => void;
  onSelectChange: (formField: keyof FormState) => (event: SelectChangeEvent<unknown>) => void;
  onPriorityChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const EditSchoolCardApplication = ({
  formState,
  handleOnCancel,
  handleOnSave,
  onPriorityChange,
  onSelectChange,
}: EditSchoolCardApplicationProps) => {
  const { deadlineType, applicationType, applicationStatus, admissionStatus, decision } = formState;

  const isCommonAppApplication = formState.applicationType === ApplicationType.COMMON;

  return (
    <StyledApplication data-testid="editMode">
      <StyledApplicationSection isEdit={!isCommonAppApplication}>
        <CoreTypography sx={sxStyles.sectionTitle} variant="h6" component="h5">
          Application
        </CoreTypography>

        {applicationType && (
          <StyledCardInfoLine>
            <CoreTypography variant="subtitle2" component="p">
              Type:
            </CoreTypography>

            <CoreTypography data-testid="applicationType" variant="body2">
              {applicationType}
            </CoreTypography>
          </StyledCardInfoLine>
        )}
        {isCommonAppApplication ? (
          <StyledCardInfoLine>
            <CoreTypography variant="subtitle2" component="p">
              Admission plan:
            </CoreTypography>

            <CoreTypography data-testid="selectedRound" variant="body2">
              {deadlineType}
            </CoreTypography>
          </StyledCardInfoLine>
        ) : (
          <StyledApplicationFormControl size="small">
            <InputLabel>Admission Plan</InputLabel>

            <StyledApplicationSelect
              value={deadlineType}
              onChange={onSelectChange('deadlineType')}
              label="Deadline Type"
            >
              {Object.keys(DeadlineType).map((key) => (
                <CoreMenuItem key={key} value={DeadlineType[key as keyof typeof DeadlineType]}>
                  {(DeadlineType as Record<string, unknown>)[key] as string}
                </CoreMenuItem>
              ))}
            </StyledApplicationSelect>
          </StyledApplicationFormControl>
        )}
        <StyledApplicationFormControl size="small">
          <InputLabel>Application status</InputLabel>
          <StyledApplicationSelect
            value={applicationStatus}
            onChange={onSelectChange('applicationStatus')}
            label="Application status"
          >
            {Object.keys(ApplicationStatus).map((key) => (
              <CoreMenuItem key={key} value={ApplicationStatus[key as keyof typeof ApplicationStatus]}>
                {(ApplicationStatus as Record<string, unknown>)[key] as string}
              </CoreMenuItem>
            ))}
          </StyledApplicationSelect>
        </StyledApplicationFormControl>
      </StyledApplicationSection>

      <StyledCardDivider />

      <StyledApplicationSection>
        <CoreTypography sx={sxStyles.sectionTitle} variant="h6" component="h5">
          School ranking
        </CoreTypography>
        <StyledApplicationFormControl fullWidth size="small">
          <CoreRadioGroup onChange={onPriorityChange}>
            <StyledApplicationSection>
              {Object.entries(SCHOOL_RANKING_DATA).map(([key, value]) => (
                <StyledApplicationScore key={key}>
                  <StyledApplicationScoreNumber isSelected={formState.priority === +key}>
                    <StyledApplicationScoreRadio value={key} />

                    <CoreTypography>{key}</CoreTypography>
                  </StyledApplicationScoreNumber>

                  <CoreTypography variant="body2">{value}</CoreTypography>
                </StyledApplicationScore>
              ))}
            </StyledApplicationSection>
          </CoreRadioGroup>
        </StyledApplicationFormControl>
      </StyledApplicationSection>

      <StyledCardDivider />

      <StyledApplicationSection>
        <CoreTypography sx={sxStyles.sectionTitle} variant="h6" component="h5">
          Decision
        </CoreTypography>

        <StyledApplicationFormControl size="small">
          <InputLabel>Admission status</InputLabel>

          <StyledApplicationSelect
            data-testid="admission-status-select"
            value={admissionStatus}
            onChange={onSelectChange('admissionStatus')}
            label="Admission Status"
          >
            {Object.keys(AdmissionStatus).map((key) => (
              <CoreMenuItem key={key} value={AdmissionStatus[key as keyof typeof AdmissionStatus]}>
                {(AdmissionStatus as Record<string, unknown>)[key] as string}
              </CoreMenuItem>
            ))}
          </StyledApplicationSelect>
        </StyledApplicationFormControl>

        <StyledApplicationFormControl size="small">
          <InputLabel>Student decision</InputLabel>
          <StyledApplicationSelect value={decision} onChange={onSelectChange('decision')} label="Student decision">
            {Object.keys(Decision).map((key) => (
              <CoreMenuItem key={key} value={Decision[key as keyof typeof Decision]}>
                {(Decision as Record<string, unknown>)[key] as string}
              </CoreMenuItem>
            ))}
          </StyledApplicationSelect>
        </StyledApplicationFormControl>
      </StyledApplicationSection>

      <StyledApplicationButtonGroup>
        <ActionButton size="small" variant="outlined" data-testid="cancelBtn" onClick={handleOnCancel}>
          Cancel
        </ActionButton>

        <ActionButton data-testid="saveBtn" size="small" onClick={handleOnSave}>
          Save changes
        </ActionButton>
      </StyledApplicationButtonGroup>
    </StyledApplication>
  );
};
